import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import ArticleNavbar from "@/pages/article/components/ArticleNavbar";
import ArticleFooter from "@/pages/article/components/ArticleFooter";

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main>
        <title>Article Not Found | itaobuy.team</title>
        <meta name="description" content="The article you are looking for could not be found. Browse our complete collection of itaobuy sheet guides and comparisons." />
        <meta name="keywords" content="itaobuy sheet, itaobuy spreadsheet, itaobuy guides" />
        <link rel="canonical" href="https://itaobuy.team/" />
        <ArticleNavbar />
        <div className="pt-24 pb-20 px-4 text-center">
          <div className="max-w-xl mx-auto">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
            <p className="text-sm text-foreground-600 mb-6">We couldn&apos;t find the article you were looking for. It may have been moved or the link might be incorrect.</p>
            <Link to="/" className="inline-flex px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Back to Itaobuy Sheet Hub
            </Link>
          </div>
        </div>
        <ArticleFooter />
      </main>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "image": article.imageUrl,
    "author": {
      "@type": "Organization",
      "name": "itaobuy.team",
      "url": "https://itaobuy.team/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "itaobuy.team",
      "url": "https://itaobuy.team/",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg"
      }
    },
    "datePublished": "2026-06-01",
    "dateModified": "2026-06-28",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://itaobuy.team/${article.slug}`
    }
  };

  const isSafety = article.tag.toLowerCase().includes("safety");
  const isComparison = article.tag.toLowerCase().includes("comparison");
  const isCategory = article.tag.toLowerCase().includes("category");
  const isTips = article.tag.toLowerCase().includes("tips");

  return (
    <main>
      <title>{article.title} | itaobuy.team</title>
      <meta name="description" content={article.description} />
      <meta name="keywords" content={`itaobuy sheet, itaobuy spreadsheet, ${article.title.toLowerCase()}`} />
      <link rel="canonical" href={`https://itaobuy.team/${article.slug}`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <ArticleNavbar />

      {/* Article Header with image */}
      <header className="pt-20 pb-0 px-0 bg-background-50">
        <div className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-black/30"></div>
        </div>
        <div className="max-w-3xl mx-auto px-4 md:px-6 pb-8 md:pb-12 -mt-20 relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs md:text-sm text-primary-600 hover:text-primary-700 transition-colors mb-4 cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            Back to Itaobuy Sheet Hub
          </Link>
          <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${article.tagColor} mb-3`}>
            {article.tag}
          </span>
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex items-center gap-3 text-xs md:text-sm text-foreground-500">
            <span>Updated {article.date}</span>
            <span>&bull;</span>
            <span>{article.readTime}</span>
            <span>&bull;</span>
            <span>By itaobuy.team</span>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="prose-custom space-y-6 text-foreground-800 leading-relaxed text-sm md:text-base">

            {/* Introduction */}
            <Section>
              <p>{article.description}</p>
              <p>
                Whether you are a first-time user exploring the{" "}
                <strong className="text-foreground-950">itaobuy spreadsheet</strong>{" "}
                platform or an experienced shopper looking to refine your strategy, this article covers everything you need to know. The itaobuy sheet ecosystem has grown significantly in 2026, and keeping up with the latest practices, tools, and safety measures is essential for making smart purchasing decisions.
              </p>
            </Section>

            {/* Key Insights */}
            <Section title="Key Takeaways">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: "ri-lightbulb-line", text: "Understand how itaobuy sheet tools streamline product comparison" },
                  { icon: "ri-shield-check-line", text: "Learn verification methods to avoid unreliable itaobuy spreadsheet sources" },
                  { icon: "ri-bar-chart-2-line", text: "Discover data-driven strategies for better purchasing decisions" },
                  { icon: "ri-timer-line", text: "Save hours of manual browsing with structured itaobuy sheet views" },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-background-100 rounded-xl border border-background-200/70">
                    <span className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <p className="text-xs md:text-sm text-foreground-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* Core Content based on tag type */}
            {isSafety && <SafetyContent />}
            {isComparison && <ComparisonContent />}
            {isCategory && <CategoryContent article={article} />}
            {isTips && <TipsContent />}

            {/* All articles get these general sections too */}
            {isSafety && (
              <Section title="Best Practices for Safe Itaobuy Sheet Usage">
                <p>
                  Beyond basic verification, there are several daily habits that significantly improve your safety when using any{" "}
                  <strong className="text-foreground-950">itaobuy spreadsheet</strong>. First, always check the last-updated timestamp on any listing — stale data can lead to dead links, outdated prices, or listings from sellers who are no longer active. A quality itaobuy sheet platform refreshes its data at regular intervals throughout the day.
                </p>
                <p>
                  Second, cross-reference seller ratings across multiple categories. A seller with consistently high scores across Shoes, Jackets, and Accessories is more trustworthy than one who only appears in a single niche. The itaobuy sheet comparison view makes this cross-referencing straightforward.
                </p>
                <p>
                  Third, pay attention to the listing completeness score. Verified itaobuy spreadsheet entries include detailed product descriptions, multiple images, size charts where applicable, and clear shipping information. Incomplete listings — even from otherwise trusted sellers — should be approached with additional caution.
                </p>
              </Section>
            )}

            {isComparison && (
              <Section title="Choosing the Right Itaobuy Sheet for Your Needs">
                <p>
                  Different itaobuy spreadsheet configurations excel at different tasks. Casual shoppers benefit most from simplified views with fewer columns and easier navigation. Professional buyers and boutique owners need advanced filtering, bulk export capabilities, and detailed seller analytics. The key is matching the itaobuy sheet complexity to your actual usage patterns.
                </p>
                <p>
                  Speed is another critical factor. Some itaobuy sheet platforms prioritize loading speed above all else, making them ideal for quick browsing on mobile devices. Others optimize for data depth, loading richer listing details at the cost of slightly longer initial page loads. Consider your primary device and browsing habits when choosing between options.
                </p>
                <p>
                  Trust scores and verification depth vary between itaobuy sheet providers. The highest-rated platforms include multi-factor seller verification, transparent data sourcing, and clear update frequency indicators. Do not compromise on these trust features — they are the foundation of a safe shopping experience.
                </p>
              </Section>
            )}

            {isCategory && (
              <Section title={`How to Find the Best ${article.title.split(":")[0].replace("on Itaobuy Sheet", "").trim()} Deals`}>
                <p>
                  The itaobuy spreadsheet makes it uniquely easy to compare products within a specific category. Start by applying the category filter to narrow down to exactly the type of product you are looking for. Then sort by the metric that matters most to you — price for budget shoppers, seller rating for quality-focused buyers, or order volume to see what is most popular with other customers.
                </p>
                <p>
                  Pay special attention to the price-per-unit column when comparing bulk options. Many listings offer significant discounts for multi-item purchases, and the itaobuy sheet automatically calculates the per-unit cost so you can compare single-item and bulk prices on an even basis. This is especially valuable for boutique owners and resellers who purchase in volume.
                </p>
                <p>
                  Material and specification columns become particularly important in category-specific searches. The itaobuy spreadsheet includes detailed product attributes that go beyond basic price and rating data — fabric composition, sizing charts, color options, and shipping weight are all available for comparison when provided by the seller.
                </p>
              </Section>
            )}

            {isTips && (
              <Section title="Advanced Itaobuy Sheet Techniques">
                <p>
                  Power users of the itaobuy spreadsheet platform have developed several advanced techniques that dramatically improve efficiency. One of the most effective is creating saved filter presets for different shopping scenarios — a quick-switch setup for bargain hunting, another for premium quality sourcing, and a third for bulk order analysis.
                </p>
                <p>
                  Cross-category comparison is another underutilized technique. The itaobuy sheet allows you to view products from multiple categories in a single spreadsheet view, revealing patterns and opportunities that are invisible when browsing categories one at a time. For example, comparing Hoodies across different sellers often reveals that sellers who excel in one category also offer competitive pricing in adjacent categories.
                </p>
                <p>
                  Price history tracking — available on most itaobuy sheet platforms — provides a significant advantage for timing your purchases. Products in seasonal categories like Jackets or Hoodies often follow predictable price cycles. By monitoring the price history chart over several weeks, you can identify the optimal moment to buy and potentially save 15-30% compared to peak-season pricing.
                </p>
              </Section>
            )}

            {/* Common FAQ */}
            <Section title="Frequently Asked Questions">
              <div className="space-y-3">
                {[
                  { q: "Is itaobuy sheet free to use?", a: "Yes — browsing and comparing products through the itaobuy spreadsheet platform is completely free. You only pay when you decide to purchase a product through a seller's listing." },
                  { q: "How often is the data updated?", a: "Data is refreshed at regular intervals throughout the day. Pricing, availability, and seller ratings are kept current for accurate decision-making." },
                  { q: "Can I use it on mobile?", a: "Absolutely. The itaobuy spreadsheet platform is fully responsive and works on smartphones, tablets, and desktop browsers." },
                  { q: "How do I know sellers are trustworthy?", a: "The itaobuy sheet includes a seller verification system with trust scores based on transaction history, customer feedback, and listing accuracy." },
                ].map((faq, i) => (
                  <div key={i} className="p-4 bg-background-100 rounded-xl border border-background-200/70">
                    <h4 className="font-semibold text-sm text-foreground-900 mb-2">Q: {faq.q}</h4>
                    <p className="text-xs md:text-sm text-foreground-600">A: {faq.a}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* Bottom CTA */}
            <div className="mt-10 p-6 md:p-8 bg-primary-50 rounded-2xl border border-primary-200 text-center">
              <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                Ready to Try Itaobuy Sheet?
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                Start comparing products across all categories with the most trusted itaobuy spreadsheet platform.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto px-7 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Go to Itaobuy Sheet
                </a>
                <Link
                  to="/"
                  className="w-full sm:w-auto px-7 py-3 bg-background-50 border border-background-300 text-foreground-700 font-semibold text-sm rounded-full hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Back to Comparison Hub
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      <ArticleFooter />
    </main>
  );
}

/* Reusable section wrapper */
function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section className="py-2">
      {title && (
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

/* Content type variants */
function SafetyContent() {
  return (
    <Section title="Understanding Itaobuy Sheet Safety">
      <p>
        Safety is the number one concern for anyone using an{" "}
        <strong className="text-foreground-950">itaobuy spreadsheet</strong>, and for good reason. With the growing popularity of the platform in 2026, understanding the verification systems and trust mechanisms built into the itaobuy sheet ecosystem is essential for protecting yourself while shopping.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
        <div className="p-5 bg-primary-50/50 rounded-xl border border-primary-200/50">
          <h3 className="flex items-center gap-2 font-semibold text-sm text-primary-800 mb-3">
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-check-line"></i>
            </span>
            Safe Practices
          </h3>
          <ul className="space-y-2 text-xs md:text-sm text-foreground-700">
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-primary-500 mt-0.5">
                <i className="ri-checkbox-circle-fill text-sm"></i>
              </span>
              Use only verified itaobuy spreadsheets with transparent data sources
            </li>
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-primary-500 mt-0.5">
                <i className="ri-checkbox-circle-fill text-sm"></i>
              </span>
              Check seller trust scores before clicking through to purchase
            </li>
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-primary-500 mt-0.5">
                <i className="ri-checkbox-circle-fill text-sm"></i>
              </span>
              Verify listing freshness — data updated within the last 24 hours
            </li>
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-primary-500 mt-0.5">
                <i className="ri-checkbox-circle-fill text-sm"></i>
              </span>
              Cross-reference with multiple itaobuy sheet sources
            </li>
          </ul>
        </div>
        <div className="p-5 bg-accent-50/50 rounded-xl border border-accent-200/50">
          <h3 className="flex items-center gap-2 font-semibold text-sm text-accent-800 mb-3">
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <i className="ri-close-line"></i>
            </span>
            Red Flags
          </h3>
          <ul className="space-y-2 text-xs md:text-sm text-foreground-700">
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-accent-500 mt-0.5">
                <i className="ri-close-circle-fill text-sm"></i>
              </span>
              Unverified listings with no seller history
            </li>
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-accent-500 mt-0.5">
                <i className="ri-close-circle-fill text-sm"></i>
              </span>
              Prices significantly below market average without explanation
            </li>
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-accent-500 mt-0.5">
                <i className="ri-close-circle-fill text-sm"></i>
              </span>
              Incomplete product descriptions or missing images
            </li>
            <li className="flex items-start gap-2">
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center text-accent-500 mt-0.5">
                <i className="ri-close-circle-fill text-sm"></i>
              </span>
              Stale listings not updated in over a week
            </li>
          </ul>
        </div>
      </div>
    </Section>
  );
}

function ComparisonContent() {
  return (
    <Section title="Itaobuy Sheet Platform Comparison Criteria">
      <p>
        When comparing different{" "}
        <strong className="text-foreground-950">itaobuy spreadsheet</strong> platforms, there are several key dimensions that separate the best options from the rest. Speed, accuracy, trust verification, and category coverage all play critical roles in determining which itaobuy sheet is right for your specific needs.
      </p>
      <div className="overflow-x-auto my-4">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b-2 border-background-300">
              <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Criteria</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">What to Look For</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Why It Matters</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Data Freshness", "Updated multiple times daily", "Ensures prices and availability are current"],
              ["Verification Depth", "Multi-factor seller checks", "Reduces risk of unreliable listings"],
              ["Category Coverage", "14+ product categories", "More comparison options across niches"],
              ["Mobile Experience", "Fully responsive design", "Shopping on the go without friction"],
              ["Filter Flexibility", "Advanced multi-column sorting", "Faster product discovery"],
              ["Export Options", "CSV export for analysis", "Custom analysis in your own tools"],
            ].map((row, i) => (
              <tr key={i} className="border-b border-background-200/70">
                <td className="py-3 px-4 font-medium text-foreground-900">{row[0]}</td>
                <td className="py-3 px-4 text-foreground-600">{row[1]}</td>
                <td className="py-3 px-4 text-primary-700 font-medium text-xs">{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4">
        The best itaobuy spreadsheet platforms consistently perform well across all six criteria. Do not settle for a platform that excels in one area while neglecting others — a balanced approach to all comparison dimensions produces the most reliable and satisfying shopping experience. Visit the{" "}
        <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
          itaobuy comparison hub
        </Link>{" "}
        to see our latest rankings and detailed platform assessments.
      </p>
    </Section>
  );
}

function CategoryContent({ article }: { article: { title: string; description: string } }) {
  return (
    <Section title={`Why ${article.title.split(":")[0].replace("on Itaobuy Sheet", "").trim()} Matter in the Itaobuy Spreadsheet Ecosystem`}>
      <p>
        Product categories are the backbone of the itaobuy sheet experience. Each category in the itaobuy spreadsheet opens a dedicated view with listings curated specifically for that product type. This specialization means the comparison data — pricing tiers, seller ratings, material specifications, and shipping options — is always relevant to the products you are actually interested in.
      </p>
      <p>
        The itaobuy sheet category system does more than just organize products. It enables apples-to-apples comparisons that would be impossible across mixed product types. When you filter down to a single category, every row in the spreadsheet represents a directly comparable product, and the column data — price, rating, shipping time, order volume — becomes genuinely meaningful for decision-making.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
        {[
          { icon: "ri-price-tag-3-line", title: "Price Transparency", desc: "Compare prices across sellers within a single category for true market-rate visibility." },
          { icon: "ri-star-line", title: "Seller Specialization", desc: "Identify sellers who specialize in specific categories and consistently deliver quality products." },
          { icon: "ri-truck-line", title: "Shipping Optimization", desc: "Compare shipping options and delivery estimates for products with similar weights and dimensions." },
          { icon: "ri-history-line", title: "Trend Analysis", desc: "Track category-level pricing trends to identify seasonal patterns and optimal buying windows." },
        ].map((f, i) => (
          <div key={i} className="flex gap-3 p-4 bg-background-100 rounded-xl border border-background-200/70">
            <span className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
              <i className={`${f.icon} text-lg`}></i>
            </span>
            <div>
              <h4 className="font-semibold text-sm text-foreground-900 mb-1">{f.title}</h4>
              <p className="text-xs text-foreground-600 leading-relaxed">{f.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function TipsContent() {
  return (
    <Section title="Mastering the Itaobuy Sheet Workflow">
      <p>
        Getting the most out of your{" "}
        <strong className="text-foreground-950">itaobuy spreadsheet</strong> requires more than just knowing where the sort buttons are. The difference between casual users and power shoppers comes down to workflow habits — the sequence of actions you take when approaching a new product search and the consistency with which you apply best practices.
      </p>
      <div className="space-y-4">
        {[
          {
            num: "01",
            title: "Start Broad, Narrow Fast",
            desc: "Begin with a wide category filter, then apply successive refinements — price range, seller rating minimum, shipping region — until you have a focused set of 10-20 listings to evaluate in detail.",
          },
          {
            num: "02",
            title: "Use Multi-Column Sorting Strategically",
            desc: "Sort by your primary criterion first (usually price or rating), then use secondary sort columns to break ties. The itaobuy sheet supports multi-level sorting that surfaces the best options faster.",
          },
          {
            num: "03",
            title: "Check Seller Profiles Before Clicking Through",
            desc: "Before opening a listing, scan the seller's profile for their overall trust score, number of completed transactions, and category specialization. This 5-second check prevents wasted time on unreliable listings.",
          },
          {
            num: "04",
            title: "Export Data for Offline Analysis",
            desc: "For large purchases or bulk orders, export your itaobuy spreadsheet to CSV and run your own calculations. This is especially useful for calculating total landed costs including estimated shipping and potential import duties.",
          },
          {
            num: "05",
            title: "Set Price Alerts for Target Products",
            desc: "If your itaobuy sheet platform supports it, set price alerts for products you are watching. Combined with price history charts, this ensures you never miss a significant price drop on items you care about.",
          },
        ].map((step, i) => (
          <div key={i} className="flex gap-4 p-4 bg-background-100 rounded-xl border border-background-200/70">
            <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-heading text-lg font-bold">
              {step.num}
            </span>
            <div>
              <h4 className="font-semibold text-sm text-foreground-900 mb-1">{step.title}</h4>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}